import React from 'react';
import { makeStyles } from '@material-ui/core';

import { BreadcrumbsWrapper, Chip, Grid } from '@ha/dsl';
import { useIntl } from 'ha/i18n';

import HermesMeta from 'ha/components/HermesMeta';
import { TOPIC_CLUSTER_LABEL } from 'ha/constants/CommunityCategories';
import { useLocation, useHistory } from 'ha/helpers/router';
import { HermesProps } from 'ha/modules/Hermes';
import { LinkData, TopicCluster } from 'ha/modules/Hermes/types';
import { H1 } from 'ha/ui/Headers';

import CommunityCard from './CommunityCard';
import CommunityCTACard from './CommunityCTACard';
import { HermesData } from '../types';
import CommunityBreadcrumbs from './CommunityBreadcrumbs';

interface Props {
  linkData: LinkData[];
}

const useStyles = makeStyles(theme => ({
  card: {
    [theme.breakpoints.up('lg')]: {
      flexGrow: 1,
      maxWidth: 'initial',
    },
  },
}));

const CommunityPageLayout: React.FC<Props & HermesProps<HermesData>> = ({
  data: { blogList, header = {}, cta = {}, meta = {} },
  linkData,
}) => {
  const { lang, T, urlResolver } = useIntl();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const selectedCategory = location.query.category;

  const categories = blogList.reduce<TopicCluster[]>((memo, item) => {
    if (item.topicCluster && !memo.includes(item.topicCluster)) {
      memo.push(item.topicCluster);
    }

    return memo;
  }, []);

  const filteredBlogList = blogList.filter(item => {
    if (selectedCategory) {
      return item.topicCluster === selectedCategory;
    }

    return true;
  });

  const handleTagClick = React.useCallback(
    (category: string, isSelected: boolean) => () => {
      history.push(
        urlResolver.getCommunityUrl(lang, isSelected ? undefined : category),
      );
    },
    [history, lang, urlResolver],
  );

  const cardElements = filteredBlogList.map((blogInfo, index) => {
    const kind =
      index === 0
        ? 'primary'
        : index % 6 === 4 || index % 6 === 5
        ? 'featured'
        : 'default';

    return (
      <Grid
        item
        key={blogInfo.pathname}
        className={classes.card}
        xs={12}
        md={kind === 'default' ? 6 : 12}
        lg={kind === 'default' ? 3 : 5}
        data-test-locator="Community Card"
      >
        <CommunityCard
          kind={kind}
          imageUrl={blogInfo.headerImage}
          title={blogInfo.title}
          description={blogInfo.description}
          linkUrl={blogInfo.pathname}
        />
      </Grid>
    );
  });

  const ctaCardElement = (
    <Grid
      item
      key="community-cta-card"
      className={classes.card}
      xs={12}
      md={6}
      lg={3}
      data-test-locator="Community CTA Card"
    >
      <CommunityCTACard {...cta} />
    </Grid>
  );

  return (
    <React.Fragment>
      <HermesMeta
        metaDescription={meta.description || ''}
        metaTitle={meta.title || ''}
        linkData={linkData}
      />
      <BreadcrumbsWrapper>
        <CommunityBreadcrumbs />
      </BreadcrumbsWrapper>
      <div className="m-x-1_5">
        <div className="container-grid">
          <H1
            className="m-t-2_5 m-b-1 color-brand"
            data-test-locator="Community Title"
          >
            {header.heading || T('HousingAnywhere Community')}
          </H1>
          <div
            className="m-b-1 text-bold"
            data-test-locator="Community SubTitle"
          >
            {header.subtitle || T('All you need to survive without moms')}
          </div>
          <div className="m-b-2">
            <Grid container spacing={1}>
              {categories.map(category => (
                <Grid item key={category}>
                  <Chip
                    color={
                      category === selectedCategory ? 'primary' : undefined
                    }
                    label={T(TOPIC_CLUSTER_LABEL[category])}
                    clickable
                    onClick={handleTagClick(
                      category,
                      category === selectedCategory,
                    )}
                  />
                </Grid>
              ))}
            </Grid>
          </div>
          <Grid container spacing={3}>
            {[
              ...cardElements.slice(0, 2),
              ctaCardElement,
              ...cardElements.slice(2),
            ]}
          </Grid>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CommunityPageLayout;
